'use client'

// libraries
import clsx from 'clsx'
import { useState, useEffect, useMemo, Suspense, useRef } from 'react'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import Link from '/react4xp/components/Link'
import Input from '/react4xp/components/Form/Input'
import Loading from '/react4xp/components/Loading'
import Portal from '/react4xp/components/Portal'

// svg
import { ChevronDown, Close, CloseLight, Clock, MapPin } from '/react4xp/components/Icons/Ux'
import { SingleCourse, OneYear, Bachelor, Master, Profession, FurtherEducation } from '/react4xp/components/Icons/CourseLevels'

// css
import './index.scss'

// utils
import { matchItems } from '/react4xp/utils/matchStr'

// animations
import { useResultsAnimation } from '/react4xp/components/Animations/useResultsAnimation'

// Simple URL parameter management without URLSearchParams
const parseUrlParams = (search: string): Record<string, string> => {
    const params: Record<string, string> = {}
    if (!search || search === '?') return params
    
    const queryString = search.startsWith('?') ? search.substring(1) : search
    if (!queryString) return params
    
    queryString.split('&').forEach(pair => {
        const [key, value] = pair.split('=')
        if (key) {
            params[decodeURIComponent(key)] = decodeURIComponent(value || '')
        }
    })
    
    return params
}

const buildUrlParams = (params: Record<string, string>): string => {
    const pairs: string[] = []
    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
    })
    return pairs.join('&')
}

// Client-only wrapper to prevent hydration mismatches
function ClientOnly({ children }: { children: React.ReactNode }) {
    const [hasMounted, setHasMounted] = useState(false)

    useEffect(() => {
        setHasMounted(true)
    }, [])

    if (!hasMounted) {
        return <div></div>
    }

    return <>{children}</>
}

// types
interface CoursesOverviewProps {
    className?: string
    data?: {
        codes?: string[]
        codeLabel?: string
        href?: string
        title?: string
        studyLevel?: {
            items?: {
                label?: string
                icon?: string
                type?: string
            }[]
        }
        studyPoints?: string
        duration?: string[]
        location?: Array<{ name: string; url?: string }>
        extraLocations?: Array<{ name: string; url?: string }>
        areasOfInterest?: string[]
        organization?: string[]
        implementation?: string[]
        openForApplication?: boolean
        searchTags?: string[]
    }[] | string
    searchBox?: {
        title?: string
        description?: string
        searchBar?: {
            helper?: string
            placeholder?: string
            cta?: string
        }
    }
    howManyPerPage?: number
    singleStrings?: {
        showing?: string
        studyOffers?: string
        filterTitles?: {
            areasOfInterest?: string
            locations?: string
            studyLevels?: string
            organizations?: string
            implementations?: string
            application?: string
        }
        applicationFilterOption?: string
        filterCtaMobile?: {
            filter?: string
            filtering?: string
            activeFilters?: string
            apply?: string
        }
        loadMore?: string
        loading?: string
        clearSearch?: string
    }
    hideFilters?: boolean
}

function CoursesOverviewInternal({
    className,
    data,
    searchBox,
    howManyPerPage = 20,
    singleStrings,
    hideFilters = false
}: CoursesOverviewProps) {

    // extract unique filter values from data
    const safeData = useMemo((): Array<{
        codes?: string[]
        codeLabel?: string
        href?: string
        title?: string
        studyLevel?: {
            items?: {
                label?: string
                icon?: string
                type?: string
            }[]
        }
        studyPoints?: string
        duration?: string[]
        location?: Array<{ name: string; url?: string }>
        extraLocations?: Array<{ name: string; url?: string }>
        areasOfInterest?: string[]
        organization?: string[]
        implementation?: string[]
        searchTags?: string[]
        openForApplication?: boolean
    }> => {
        if (Array.isArray(data)) {
            return data
        } else if (typeof data === 'string') {
            try {
                return JSON.parse(data)
            } catch (error) {
                return []
            }
        }
        return []
    }, [data])
    
    const studyLevelOrder = ['Master', 'Bachelor', 'Årsstudium', 'Videreutdanning', 'Enkeltemne', 'Profesjonsstudium'];
    const {
        areasOfInterest,
        locations,
        studyLevels,
        organizations,
        implementations
    } = useMemo(() => {
        // Collect all study level items with their full data
        const allStudyLevelItems = safeData.flatMap(item => item.studyLevel?.items || [])
        
        // Remove duplicates based on label, keeping the first occurrence
        const uniqueStudyLevelItems = Array.from(
            new Map(allStudyLevelItems.map(item => [item.label, item])).values()
        )
        
        // Sort by type following studyLevelOrder priority
        const sortedStudyLevelItems = uniqueStudyLevelItems.sort((a, b) => {
            const indexA = studyLevelOrder.indexOf(a.type || '')
            const indexB = studyLevelOrder.indexOf(b.type || '')
            
            // If type not found in order, put it at the end
            if (indexA === -1 && indexB === -1) return 0
            if (indexA === -1) return 1
            if (indexB === -1) return -1
            
            return indexA - indexB
        })
        
        // Extract labels
        const studyLevels = sortedStudyLevelItems.map(item => item.label).filter((label): label is string => Boolean(label))
        
        return {
            areasOfInterest: Array.from(new Set(safeData.flatMap(item => item.areasOfInterest || []))),
            locations: Array.from(new Set(safeData.flatMap(item => (item.location || []).map(l => l.name)))),
            studyLevels: studyLevels,
            organizations: Array.from(new Set(safeData.flatMap(item => item.organization || []))),
            implementations: Array.from(new Set(safeData.flatMap(item => item.implementation || []))).sort()
        }
    }, [safeData])

    // filter configurations
    const filterConfigs: Array<{
        key: 'areasOfInterest' | 'locations' | 'studyLevels' | 'organizations' | 'implementations' | 'soknad'
        title: string
        options: string[]
    }> = [
        {
            key: 'areasOfInterest' as const,
            title: singleStrings?.filterTitles?.areasOfInterest || '',
            options: areasOfInterest.filter((item): item is string => Boolean(item))
        },
        {
            key: 'organizations' as const,
            title: singleStrings?.filterTitles?.organizations || '',
            options: organizations.filter((item): item is string => Boolean(item))
        },
        {
            key: 'locations' as const,
            title: singleStrings?.filterTitles?.locations || '',
            options: locations.filter((item): item is string => Boolean(item))
        },
        {
            key: 'studyLevels' as const,
            title: singleStrings?.filterTitles?.studyLevels || '',
            options: studyLevels.filter((item): item is string => Boolean(item))
        },
        {
            key: 'implementations' as const,
            title: singleStrings?.filterTitles?.implementations || '',
            options: implementations.filter((item): item is string => Boolean(item))
        },
        {
            key: 'soknad' as const,
            title: singleStrings?.filterTitles?.application || '',
            options: (safeData.length > 0 ? [singleStrings?.applicationFilterOption] : []).filter((item): item is string => Boolean(item))
        }
    ]

    // URL parameter mapping - automatically derived from filter config keys
    const urlParamMap = useMemo(() => {
        const camelToKebab = (str: string) => {
            return str.replace(/([A-Z])/g, '-$1').toLowerCase()
        }
        
        const baseMap = filterConfigs.reduce((acc, config) => {
            // Convert camelCase to kebab-case for URL parameters
            let urlParam = camelToKebab(config.key)
            
            // Special cases for better URL readability
            if (config.key === 'locations') urlParam = 'location'
            if (config.key === 'studyLevels') urlParam = 'study-level'
            
            acc[config.key] = urlParam
            return acc
        }, {} as Record<string, string>)
        
        // Add search parameter
        return {
            ...baseMap,
            search: 'search'
        }
    }, [filterConfigs])

    // Custom URL management hooks
    const getSearchParams = () => {
        if (typeof window === 'undefined' || !window.location) return {}
        return parseUrlParams(window.location.search)
    }

    const updateUrl = (params: Record<string, string>) => {
        if (typeof window === 'undefined' || !window.location) return
        
        const queryString = buildUrlParams(params)
        const newUrl = queryString 
            ? `${window.location.pathname}?${queryString}`
            : window.location.pathname
        
        window.history.pushState({}, '', newUrl)
    }

    // function to convert filter state to URL parameters
    const filtersToUrlParams = (filters: typeof activeFilters, searchQuery?: string) => {
        const params: Record<string, string> = {}
        
        Object.entries(filters).forEach(([key, values]) => {
            if (values.length > 0) {
                const paramKey = urlParamMap[key as keyof typeof urlParamMap]
                const paramValue = values.join('-')
                params[paramKey] = paramValue
            }
        })
        
        // Add search parameter if it exists
        if (searchQuery && searchQuery.trim()) {
            params['search'] = searchQuery.trim()
        }
        
        return params
    }

    // function to convert URL parameters to filter state
    const urlParamsToFilters = () => {
        const searchParams = getSearchParams()
        const filters: {
            areasOfInterest: string[]
            locations: string[]
            studyLevels: string[]
            organizations: string[]
            implementations: string[]
            soknad: string[]
        } = {
            areasOfInterest: [],
            locations: [],
            studyLevels: [],
            organizations: [],
            implementations: [],
            soknad: []
        }
        
        Object.entries(urlParamMap).forEach(([filterKey, paramKey]) => {
            if (filterKey === 'search') return // Skip search in filters
            
            const paramValue = searchParams[paramKey]
            if (paramValue) {
                filters[filterKey as keyof typeof filters] = paramValue.split('-')
            }
        })
        
        return filters
    }

    // function to get search query from URL
    const getSearchFromUrl = () => {
        const searchParams = getSearchParams()
        return searchParams['search'] || ''
    }

    // state for expanded sidebar items - all expanded by default
    const [expandedItems, setExpandedItems] = useState<string[]>(
        filterConfigs.map(config => config.key)
    )

    // toggle sidebar item expansion
    const toggleSidebarItem = (itemKey: string) => {
        setExpandedItems(prev => 
            prev.includes(itemKey)
                ? prev.filter(key => key !== itemKey)
                : [...prev, itemKey]
        )
    }

    // state for active filters - initialize from URL parameters
    const [activeFilters, setActiveFilters] = useState<{
        areasOfInterest: string[]
        locations: string[]
        studyLevels: string[]
        organizations: string[]
        implementations: string[]
        soknad: string[]
    }>(() => urlParamsToFilters())

    // state for search query - initialize from URL
    const [searchQuery, setSearchQuery] = useState<string>(() => getSearchFromUrl())
    
    // state for input value (what user is typing) - initialize from URL
    const [inputValue, setInputValue] = useState<string>(() => getSearchFromUrl())

    // state for mobile filters modal
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
    
    // state for sidebar sticky
    const [isSidebarSticky, setIsSidebarSticky] = useState(false)
    const sidebarWrapperRef = useRef<HTMLDivElement>(null)

    // mobile filters handlers
    const openMobileFilters = () => {
        setIsMobileFiltersOpen(true)

        setTimeout(() => {
            document.querySelector('#portal')?.classList.add('is-open')
        }, 100)
    }

    const closeMobileFilters = () => {
        document.querySelector('#portal')?.classList.remove('is-open')
        setTimeout(() => {
            setIsMobileFiltersOpen(false)
        }, 400)
    }

    // toggle filter function
    const toggleFilter = (category: keyof typeof activeFilters, value: string) => {
        const newFilters = {
            ...activeFilters,
            [category]: activeFilters[category].includes(value)
                ? activeFilters[category].filter(item => item !== value)
                : [...activeFilters[category], value]
        }
        
        // Use animation-controlled filtering
        animateFilterChange(() => {
            setActiveFilters(newFilters)
            
            // Update URL parameters
            const urlParams = filtersToUrlParams(newFilters, searchQuery)
            updateUrl(urlParams)
            
            // Reset pagination when filters change
            setItemsToShow(howManyPerPage)
        })
    }

    // search handler
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
        
        // Check if search query actually changed
        const trimmedInput = inputValue.trim()
        const trimmedCurrentQuery = searchQuery.trim()
        
        if (trimmedInput === trimmedCurrentQuery) {
            // Same search term - just scroll to results if on mobile, no animation needed
            if (window.innerWidth < 992) {
                setTimeout(() => {
                    const searchResults = document.getElementById('search-results')
                    if (searchResults) {
                        searchResults.scrollIntoView({ 
                            behavior: 'smooth',
                            block: 'start'
                        })
                    }
                }, 100)
            }
            return
        }
        
        // Use animation-controlled search only when search actually changes
        animateFilterChange(() => {
            // Update search query with input value
            setSearchQuery(inputValue)
            
            // Update URL parameters
            const urlParams = filtersToUrlParams(activeFilters, inputValue)
            updateUrl(urlParams)
            
            // Reset pagination when search changes
            setItemsToShow(howManyPerPage)
        })

        // Scroll to search results on mobile
        if (window.innerWidth < 992) {
            setTimeout(() => {
                const searchResults = document.getElementById('search-results')
                if (searchResults) {
                    searchResults.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    })
                }
            }, 100)
        }
    }

    // clear search handler
    const handleClearSearch = () => {
        // Use animation-controlled clear search
        animateFilterChange(() => {
            // Clear both input and search query
            setInputValue('')
            setSearchQuery('')
            
            // Update URL parameters without search query
            const urlParams = filtersToUrlParams(activeFilters, '')
            updateUrl(urlParams)
            
            // Reset pagination when search is cleared
            setItemsToShow(howManyPerPage)
        })
    }

    // state for pagination
    const [itemsToShow, setItemsToShow] = useState(howManyPerPage)

    // filter data based on active filters and search query
    const filteredData = useMemo(() => {
        const filtered = safeData.filter(item => {

            // check search query with fuzzy matching
            if (searchQuery && searchQuery.trim()) {
                const query = searchQuery.toLowerCase().trim()
                const searchableText = [item.title].concat(item.codes || []).concat(item.searchTags || []).filter(Boolean)

                const matches = matchItems(searchableText as string[], query)

                const hasMatchingStudyLevel = query.split(' ').some(word => (item.studyLevel?.items || []).some(studyLevel => studyLevel?.label?.toLowerCase()?.includes(word?.toLowerCase() || '')))

                if ((matches.length === 0) && !hasMatchingStudyLevel) return false
            }

            // check areas of interest
            if (activeFilters.areasOfInterest.length > 0) {
                const hasMatchingArea = item.areasOfInterest?.some(area => 
                    activeFilters.areasOfInterest.includes(area)
                )
                if (!hasMatchingArea) return false
            }

            // check locations
            if (activeFilters.locations.length > 0) {
                const hasMatchingLocation = item.location?.some((locationEntry) =>
                    activeFilters.locations.includes(locationEntry.name)
                )
                if (!hasMatchingLocation) return false
            }

            // check study levels
            if (activeFilters.studyLevels.length > 0) {
                const hasMatchingStudyLevel = item.studyLevel?.items?.some(studyLevel =>
                    activeFilters.studyLevels.includes(studyLevel.label as string)
                )
                if (!hasMatchingStudyLevel) return false
            }

            // check organizations
            if (activeFilters.organizations.length > 0) {
                const hasMatchingOrganization = item.organization?.some(organization => 
                    activeFilters.organizations.includes(organization)
                )
                if (!hasMatchingOrganization) return false
            }

            // check implementations
            if (activeFilters.implementations.length > 0) {
                const hasMatchingImplementation = item.implementation?.some(implementation =>
                    activeFilters.implementations.includes(implementation)
                )
                if (!hasMatchingImplementation) return false
            }

            // check open-for-application (single boolean toggle)
            if (activeFilters.soknad.length > 0 && !item.openForApplication) {
                return false
            }

            return true
        })

        // Sort alphabetically if both "Enkeltemne" and "Videreutdanning" are selected
        const hasEnkeltemne = activeFilters.studyLevels.includes('Enkeltemne')
        const hasVidereutdanning = activeFilters.studyLevels.includes('Videreutdanning')
        
        if (hasEnkeltemne && hasVidereutdanning) {
            return [...filtered].sort((a, b) => {
                const titleA = (a.title || '').toLowerCase()
                const titleB = (b.title || '').toLowerCase()
                return titleA.localeCompare(titleB)
            })
        }

        return filtered
    }, [safeData, activeFilters, searchQuery])

    // get visible data based on pagination
    const visibleData = useMemo(() => {
        return filteredData.slice(0, itemsToShow)
    }, [filteredData, itemsToShow])

    // load more handler
    const handleLoadMore = () => {
        setItemsToShow(prev => prev + howManyPerPage)
    }

    // check if there are more items to load
    const hasMoreItems = itemsToShow < filteredData.length

    // animation hook - triggers on visibleData changes (for load more and initial load)
    const { containerRef: resultsRef, animateFilterChange } = useResultsAnimation([visibleData])

    // reset pagination when howManyPerPage changes
    useEffect(() => {
        setItemsToShow(howManyPerPage)
    }, [howManyPerPage])

    // sync filters and search with URL parameters when URL changes
    useEffect(() => {
        const urlFilters = urlParamsToFilters()
        const urlSearch = getSearchFromUrl()
        setActiveFilters(urlFilters)
        setSearchQuery(urlSearch)
        setInputValue(urlSearch)
        setItemsToShow(howManyPerPage)
    }, [howManyPerPage])

    // check sidebar height and apply sticky class if needed - only on initial load
    useEffect(() => {
        const checkSidebarHeight = () => {
            if (!sidebarWrapperRef.current) return
            
            const sidebarHeight = sidebarWrapperRef.current.offsetHeight
            const windowHeight = window.innerHeight
            
            setIsSidebarSticky(sidebarHeight < windowHeight)
        }

        // Only check on mount when data is ready
        if (safeData.length > 0) {
            checkSidebarHeight()
        }
    }, [safeData.length])

    // handle escape key to close mobile filters
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMobileFiltersOpen) {
                closeMobileFilters()
            }
        }

        if (isMobileFiltersOpen) {
            document.addEventListener('keydown', handleEscapeKey)
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey)
            document.body.style.overflow = 'unset'
        }
    }, [isMobileFiltersOpen])

    const activeFiltersCount = useMemo(() => {
        return activeFilters.areasOfInterest.length + activeFilters.locations.length + activeFilters.studyLevels.length + activeFilters.organizations.length + activeFilters.implementations.length + activeFilters.soknad.length
    }, [activeFilters])

    // render sidebar content
    const renderSidebarContent = () => (
        <>
            {filterConfigs.filter(filter => filter.options.length > 0).map(filter => (
                <div
                    key={filter.key}
                    className={clsx(
                        'courses-sidebar__item',
                        { active: expandedItems.includes(filter.key) }
                    )}
                >

                    <button
                        className='courses-sidebar__item__button'
                        type='button'
                        onClick={() => toggleSidebarItem(filter.key)}
                    >

                        <ChevronDown />

                        <span className='h5'>
                            {filter.title}
                        </span>

                    </button>

                    <div className='courses-sidebar__item__content'>
                        <div>
                            <div>

                                {filter.options.map(option => (
                                    <button
                                        key={option}
                                        className={clsx(
                                            { active: activeFilters[filter.key].includes(option) },
                                            'body-small'
                                        )}
                                        type='button'
                                        onClick={() => {
                                            toggleFilter(filter.key, option)
                                        }}
                                    >

                                        <span>{option}</span>

                                        {activeFilters[filter.key].includes(option) && (
                                            <Close />
                                        )}

                                    </button>
                                ))}

                            </div>
                        </div>
                    </div>

                </div>
            ))}
        </>
    )
    
    return (
        <div className={clsx('page-studies-filter', className)}>

            <Section
                className='page-studies-filter__search-box'
                removeMarginTop
            >
                <div className='container'>
                    <RoundedBox bgColor='green'>
                        <div className='row'>
                            <div className='col-lg-10 offset-lg-1 col-xl-8 offset-xl-2'>

                                {searchBox?.title && (
                                    <h1 className='h1'>
                                        {searchBox.title}
                                    </h1>
                                )}

                                {searchBox?.description && (
                                    <div
                                        className='lead-paragraph'
                                        dangerouslySetInnerHTML={{ __html: searchBox.description }}
                                        suppressHydrationWarning
                                    />
                                )}

                                {searchBox?.searchBar && (
                                    <form
                                        className='page-studies-filter__search-box__search-bar'
                                        onSubmit={handleSearch}
                                    >

                                        <div className='input-wrapper'>

                                            <Input
                                                label={searchBox.searchBar.helper}
                                                placeholder={searchBox.searchBar.placeholder}
                                                size='large'
                                                value={inputValue}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                                            />

                                            {searchQuery && (
                                                <button
                                                    className='clear'
                                                    type='button'
                                                    aria-label={singleStrings?.clearSearch}
                                                    onClick={handleClearSearch}
                                                >
                                                    <Close />
                                                </button>
                                            )}

                                        </div>

                                        <button
                                            className='button-black'
                                            type='submit'
                                        >
                                            {searchBox.searchBar.cta}
                                        </button>

                                    </form>
                                )}
                                    
                            </div>
                        </div>
                    </RoundedBox>
                </div>
            </Section>

            <Section
                className='page-studies-filter__main'
                removeMarginTop
            >
                <div className='container'>
                    
                    <div className='row'>

                        {
                            !hideFilters && (
                                <div className='col-lg-4 courses-sidebar'>
                                    <div 
                                        ref={sidebarWrapperRef}
                                        className={clsx('courses-sidebar__wrapper', {
                                            'sticky': isSidebarSticky
                                        })}
                                    >
                                        {renderSidebarContent()}
                                    </div>
                                </div>
                            )
                        }

                        <div className={clsx(
                            'results', 
                            !hideFilters && 'col-lg-8',
                            hideFilters && 'col-lg-12 no-filters'
                        )}>

                            {
                                !hideFilters && (
                                    <button
                                        className='button-black open-mobile-filters'
                                        type='button'
                                        onClick={openMobileFilters}
                                    >
                                        {activeFiltersCount > 0 ? (
                                            <>
                                                {singleStrings?.filterCtaMobile?.filtering} ({activeFiltersCount} {singleStrings?.filterCtaMobile?.activeFilters})
                                            </>
                                        ) : (
                                            singleStrings?.filterCtaMobile?.filter
                                        )}
                                        
                                    </button>
                                )
                            }

                            <p 
                                className='h5 results__total'
                                id='search-results'
                            >
                                {singleStrings?.showing} {filteredData.length} {singleStrings?.studyOffers}
                            </p>

                            <div className='results__list' ref={resultsRef}>
                                
                                {visibleData.map((item, i) => (
                                    <a
                                        href={item.href}
                                        key={i}
                                        className={clsx(
                                            'results__list__item',
                                            hideFilters && 'no-filters'
                                        )}
                                    >
                                        <div>
                                            <span className='h3'>
                                                {item.title}
                                            </span>
                                            {
                                                item.codeLabel && (
                                                    <span className='h5'>
                                                        {item.codeLabel}
                                                    </span>
                                                )
                                            }
                                        </div>

                                        {
                                            !hideFilters && (
                                                <span className='icons body-x-small'>

                                                    {(item.studyLevel?.items || []).length > 0 ? (
                                                        <div className='icon-group'>
                                                            
                                                            {item.studyLevel?.items?.map(studyLevelItem => (
                                                                <span className='icon-wrapper' key={studyLevelItem?.label}>
    
                                                                    {studyLevelItem?.icon === 'master' && <Master />}
                                                                    {studyLevelItem?.icon === 'one-year' && <OneYear />}
                                                                    {studyLevelItem?.icon === 'profession' && <Profession />}
                                                                    {studyLevelItem?.icon === 'further-education' && <FurtherEducation />}
                                                                    {studyLevelItem?.icon === 'single-course' && <SingleCourse />}
                                                                    {studyLevelItem?.icon === 'bachelor' && <Bachelor />}
    
                                                                    <span>
                                                                        {studyLevelItem?.label}
                                                                    </span>
    
                                                                </span>
                                                            ))}

                                                            {item.studyPoints && (
                                                                <div className='icon-wrapper'>
                                                                    <span>
                                                                        {item.studyPoints}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className='icon-group icon-group--empty'>
                                                            <span className='icon-wrapper icon-wrapper--empty' />
                                                        </div>
                                                    )}

                                                    {(item.duration && (item.duration.length > 0)) ? (
                                                        <div className='icon-group'>
                                                            <span className='icon-wrapper'>

                                                                <Clock />

                                                                <span className='array'>
                                                                    {item?.duration?.map((item2, i2) => (
                                                                        <span key={i2}>
                                                                            {item2}
                                                                        </span>
                                                                    ))}
                                                                </span>

                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className='icon-group icon-group--empty'>
                                                            <span className='icon-wrapper' />
                                                        </div>
                                                    )}

                                                    {((item?.location || []).concat(item?.extraLocations || []).length > 0) ? (
                                                        <div className='icon-group'>
                                                            <span className='icon-wrapper'>
                                                                <MapPin />
                                                                <span className='array'>
                                                                    {(item?.location || []).concat(item?.extraLocations || []).map((loc, i2) => (
                                                                        <span key={i2}>
                                                                            {loc.url
                                                                                ? (
                                                                                    <Link
                                                                                        href={loc.url}
                                                                                        isExternal={false}
                                                                                        label={{ underline: loc.name }}
                                                                                    />
                                                                                )
                                                                                : loc.name}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className='icon-group icon-group--empty'>
                                                            <span className='icon-wrapper' />
                                                        </div>
                                                    )}

                                                </span>
                                            )
                                        }

                                    </a>
                                ))}

                                {hasMoreItems && (
                                    <div className='results__list__load-more'>
                                        
                                        <button
                                            className='button-hollow-black'
                                            type='button'
                                            onClick={handleLoadMore}
                                        >
                                            {singleStrings?.loadMore}
                                        </button>

                                    </div>
                                )}

                            </div>

                        </div>
                        
                    </div>

                </div>
            </Section>

            {isMobileFiltersOpen && (
                <Portal>
                    <div 
                        className='courses-filters'
                        onClick={closeMobileFilters}
                    >
                        <div 
                            className='courses-filters__modal'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='container'>

                                <div className='courses-filters__header'>
                                    <button
                                        className='close-button'
                                        type='button'
                                        onClick={closeMobileFilters}
                                        aria-label='Close filters'
                                    >
                                        <CloseLight />
                                    </button>
                                </div>

                                <div className='courses-filters__content'>
                                    {renderSidebarContent()}
                                </div>

                                <button
                                    className='button-black courses-filters__apply'
                                    type='button'
                                    onClick={closeMobileFilters}
                                >
                                    {singleStrings?.filterCtaMobile?.apply}
                                </button>

                            </div>
                        </div>
                    </div>
                </Portal>
            )}

        </div>
    )
}

export default function CoursesOverview(props: CoursesOverviewProps) {
    return (
        <Suspense fallback={<Loading text={props.singleStrings?.loading} />}>
            <ClientOnly>
                <CoursesOverviewInternal {...props} />
            </ClientOnly>
        </Suspense>
    )
}